import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, Save, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { SCREENING_SECTIONS, THERAPIST_EVALUATION_SECTIONS } from '../../lib/screeningQuestions';
import ScreeningAssessment from '../../components/assessments/ScreeningAssessment';

const DEFAULT_DOMAINS = ['Communication', 'Social Skills', 'Self-Help', 'Motor Skills'];
const DEFAULT_QUESTIONS = [
    { id: 1, text: 'Responds to name when called from a distance of 10 feet.', type: 'scale' },
    { id: 2, text: 'Uses at least 10 single words meaningfully.', type: 'scale' },
    { id: 3, text: 'Follows 2-step verbal instructions without visual prompts.', type: 'scale' },
];

export default function TakeAssessment() {
    const { submitAssessment, adminEvaluations } = useData();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const patientName = location.state?.patient?.name || 'Guest Patient';
    const assessmentTitle = location.state?.assessment?.title || 'Therapist Clinical Evaluation';
    
    const templateFromAdmin = adminEvaluations?.find(e => e.title === assessmentTitle)?.template;
    
    let activeSections = null;
    if (templateFromAdmin && templateFromAdmin.sections) {
        activeSections = templateFromAdmin.sections;
    } else {
        if (assessmentTitle === 'Child Developmental Screening') activeSections = SCREENING_SECTIONS;
        if (assessmentTitle === 'Therapist Clinical Evaluation') activeSections = THERAPIST_EVALUATION_SECTIONS;
    }
    
    const useDynamicSections = activeSections && activeSections.length > 0;

    // Dynamic config based on assessment type
    const DOMAINS = useDynamicSections ? activeSections.map(s => s.title) : DEFAULT_DOMAINS;

    const patientId = location.state?.patient?.id || 'guest';
    const draftKey = `p2g_draft_therapist_${patientId}_${assessmentTitle.replace(/\s+/g, '_')}`;

    const [restored, setRestored] = useState(false);

    const [answers, setAnswers] = useState(() => {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.answers || {};
            } catch (e) { console.error("Draft parse error", e); }
        }
        return {};
    });

    const [currentDomain, setCurrentDomain] = useState(() => {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.currentDomain || 0;
            } catch (e) { }
        }
        return 0;
    });

    const [draftSaved, setDraftSaved] = useState(false);

    // Initial restore effect
    useEffect(() => {
        if (Object.keys(answers).length > 0 || currentDomain > 0) {
            setRestored(true);
            setTimeout(() => setRestored(false), 3000);
        }
    }, []);

    // Autosave effect
    useEffect(() => {
        // Only save if there's actually something to save
        if (Object.keys(answers).length > 0 || currentDomain > 0) {
            const draft = { 
                answers, 
                currentDomain, 
                patientName, 
                assessmentTitle,
                lastSaved: new Date().toISOString()
            };
            localStorage.setItem(draftKey, JSON.stringify(draft));
            console.log("Draft autosaved for", patientName);
        }
    }, [answers, currentDomain, patientName, assessmentTitle, draftKey]);

    const handleAnswer = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    // Domain validation
    const isDomainComplete = () => {
        if (useDynamicSections) {
            const section = activeSections[currentDomain];
            return section.questions.every(q => {
                if (q.type === 'textarea' || q.type === 'text') return true; // Optional text fields
                return answers[q.id] !== undefined;
            });
        }
        return DEFAULT_QUESTIONS.every(q => answers[`${currentDomain}-${q.id}`] !== undefined);
    };

    const handleNext = () => {
        if (currentDomain < DOMAINS.length - 1) {
            setCurrentDomain(currentDomain + 1);
            window.scrollTo(0, 0);
        } else {
            const submissionData = {
                patientName: patientName,
                type: assessmentTitle,
                answers: answers,
                submitterRole: user?.role || 'Clinician',
                status: 'Completed',
                date: new Date().toISOString()
            };

            submitAssessment(submissionData);
            localStorage.removeItem(draftKey);
            navigate('/app/results', { state: { submission: submissionData } });
        }
    };

    const handlePrev = () => { if (currentDomain > 0) { setCurrentDomain(currentDomain - 1); window.scrollTo(0, 0); } };

    const handleSaveDraft = () => {
        localStorage.setItem(draftKey, JSON.stringify({ answers, currentDomain, patientName, assessmentTitle }));
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 3000);
    };

    const progress = ((currentDomain + 1) / DOMAINS.length) * 100;

    return (
        <div className="max-w-5xl mx-auto space-y-10 pb-20 px-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between bg-white/80 backdrop-blur-md p-6 rounded-[2.5rem] border border-slate-100 shadow-premium sticky top-6 z-50">
                <div className="flex items-center gap-6">
                    <Link to="/app/assessments">
                        <Button variant="ghost" size="icon" className="h-12 w-12 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors">
                            <ArrowLeft className="h-5 w-5 text-slate-600" />
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">{assessmentTitle}</h1>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">
                            Patient: <span className="text-brand-600">{patientName}</span> • {new Date().toLocaleDateString()}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-6 mt-4 md:mt-0">
                    <div className="text-right">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Clinic Progress</p>
                        <p className="text-sm font-black text-brand-600 font-outfit">{currentDomain + 1} / {DOMAINS.length} SECTIONS</p>
                    </div>
                    <Button variant="outline" onClick={handleSaveDraft} className="rounded-2xl h-12 border-slate-200 font-bold text-xs uppercase tracking-widest px-6 hover:bg-slate-50 transition-all">
                        <Save className="mr-2 h-4 w-4" /> Save
                    </Button>
                </div>
            </div>

            {/* Draft Saved Toast */}
            {(draftSaved || restored) && (
                <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-3 px-8 py-4 rounded-3xl shadow-2xl border bg-slate-900 text-white text-xs font-black uppercase tracking-widest animate-in fade-in slide-in-from-bottom-4">
                    <Check className="h-4 w-4 text-brand-400 shrink-0" /> {restored ? "Draft restored from local cloud" : "Draft saved to local cloud"}
                </div>
            )}

            {/* Progress Track */}
            <div className="flex items-center justify-between mb-2 overflow-x-auto gap-4 px-4 py-2 no-scrollbar">
                {DOMAINS.map((domain, index) => {
                    const isActive = index === currentDomain;
                    const isPast = index < currentDomain;
                    return (
                        <div key={domain} className="flex items-center gap-3 flex-shrink-0">
                            <div className={`flex items-center justify-center h-10 w-10 rounded-2xl text-xs font-black border-2 transition-all duration-300
                                ${isActive ? 'border-brand-600 bg-brand-50 text-brand-700 shadow-premium scale-110'
                                    : isPast ? 'border-green-500 bg-green-500 text-white'
                                        : 'border-slate-100 bg-white text-slate-300'}`}>
                                {isPast ? <Check className="h-4 w-4 stroke-[3px]" /> : index + 1}
                            </div>
                            <span className={`text-[10px] font-black uppercase tracking-widest ${isActive ? 'text-slate-900' : isPast ? 'text-slate-500' : 'text-slate-300'}`}>
                                {domain}
                            </span>
                            {index < DOMAINS.length - 1 && <div className={`w-6 h-0.5 ${isPast ? 'bg-green-500' : 'bg-slate-100'}`} />}
                        </div>
                    );
                })}
            </div>

            {/* Form Area */}
            <div className="min-h-[600px]">
                {useDynamicSections ? (
                    <ScreeningAssessment 
                        section={activeSections[currentDomain]} 
                        answers={answers} 
                        onChange={handleAnswer} 
                    />
                ) : (
                    <Card className="border-0 shadow-premium rounded-[3rem] overflow-hidden bg-white/50 backdrop-blur-sm border border-slate-50">
                        <CardHeader className="p-10 bg-slate-50/50 border-b border-slate-100 pb-8">
                            <CardTitle className="text-2xl font-black text-slate-900 font-outfit uppercase tracking-tight">{DOMAINS[currentDomain]} Items</CardTitle>
                            <p className="text-slate-400 font-bold text-xs uppercase tracking-widest mt-2 font-bold">Standard Clinical Metric Assessment</p>
                        </CardHeader>
                        <CardContent className="p-0 divide-y divide-slate-50">
                            {DEFAULT_QUESTIONS.map((q, idx) => (
                                <div key={q.id} className="p-10 hover:bg-white transition-all group">
                                    <div className="mb-8 flex items-start gap-4">
                                        <span className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-xl bg-slate-900 text-[10px] font-black text-white">{idx + 1}</span>
                                        <span className="text-slate-900 font-bold text-lg leading-snug">{q.text}</span>
                                    </div>
                                    <div className="ml-12 grid sm:grid-cols-4 gap-4">
                                        {[{ label: 'Independent', score: '3', desc: 'No prompts' },
                                        { label: 'Prompted', score: '2', desc: 'Verbal/Visual' },
                                        { label: 'Assisted', score: '1', desc: 'Physical help' },
                                        { label: 'Not Assessed', score: '0', desc: 'N/A or refused' }].map(opt => {
                                            const ansKey = `${currentDomain}-${q.id}`;
                                            const isSelected = answers[ansKey] === opt.score;
                                            return (
                                                <label key={opt.score} className="cursor-pointer" onClick={() => handleAnswer(ansKey, opt.score)}>
                                                    <input type="radio" name={ansKey} value={opt.score} className="peer sr-only"
                                                        checked={isSelected} onChange={() => handleAnswer(ansKey, opt.score)} />
                                                    <div className={`rounded-2xl border-2 p-5 transition-all bg-white text-center h-full flex flex-col justify-center
                                                        ${isSelected ? 'border-brand-600 bg-brand-50 shadow-premium ring-4 ring-brand-100' : 'border-slate-50 hover:border-brand-200 hover:bg-brand-50/10'}`}>
                                                        <p className={`font-black text-[10px] uppercase tracking-widest ${isSelected ? 'text-brand-700' : 'text-slate-400'}`}>{opt.label}</p>
                                                        <p className="text-[10px] font-bold text-slate-300 mt-1 uppercase leading-none">{opt.desc}</p>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                            <div className="p-10 bg-slate-50/30">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 ml-1">Clinical Remarks & Context</label>
                                <textarea
                                    className="w-full h-32 rounded-[2rem] border-0 bg-white p-6 text-sm font-medium focus:ring-4 focus:ring-brand-50 transition-all placeholder:text-slate-200 shadow-sm"
                                    placeholder="Add observational notes, environmental factors, or specific challenges..."
                                ></textarea>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between p-2">
                <Button 
                    variant="outline" 
                    onClick={handlePrev} 
                    disabled={currentDomain === 0}
                    className="h-16 rounded-[1.5rem] px-8 border-slate-200 font-black text-xs uppercase tracking-widest text-slate-400 disabled:opacity-0"
                >
                    <ChevronLeft className="mr-3 h-5 w-5" /> Back
                </Button>
                
                <div className="flex items-center gap-4">
                    {!isDomainComplete() && (
                        <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest animate-pulse">Required items pending</p>
                    )}
                    <Button 
                        onClick={handleNext} 
                        disabled={!isDomainComplete()}
                        className="h-16 rounded-[1.5rem] px-12 bg-brand-600 hover:bg-brand-700 text-white font-black text-xs uppercase tracking-[0.2em] shadow-premium transition-all disabled:opacity-50"
                    >
                        {currentDomain === DOMAINS.length - 1 ? 'Finalize Report' : 'Next Section'}
                        {currentDomain !== DOMAINS.length - 1 && <ChevronRight className="ml-3 h-5 w-5" />}
                    </Button>
                </div>
            </div>
        </div>
    );
}
